import { Timeline } from "../tools/timeline";
import { Instrument } from "../track/instrument";
import { Note } from "./note";
import { DetectedChord } from "./chord";
import type { AnalysisChord } from "./analyzer-types";

export type NoteSpan = {
  tick: number;
  durationTicks: number;
  velocity: number;
  note: Note;
  /** Fingering: 1 = thumb … 5 = pinky. */
  finger?: number;
};

/** Harmonic context carried by an annotated note (from the analyzer API). */
export type NoteHarmonyInfo = {
  chordId: string;
  symbol: string;
  romanNumeral: string;
  /** Role of this note in the chord: "root", "3rd", "5th", "7th", "non-chord", etc. */
  tone: string;
};

/**
 * A note enriched with harmonic and structural context from the analyzer API.
 * Extends NoteSpan so it is assignable everywhere a NoteSpan is expected
 * (Lane, Scheduler, piano-roll), consumers that don't need annotations
 * simply ignore the extra fields.
 */
export type AnnotatedNote = NoteSpan & {
  /**
   * L'orthographe que l'analyseur donne à cette note, sans son octave.
   *
   * `Note` ne connaît qu'un numéro de touche, et un numéro de touche ne dit
   * pas si l'on joue un `si♭` ou un `la♯`. L'analyseur, lui, le sait : d'une
   * partition il tient la lettre que le graveur a écrite, et d'un fichier MIDI
   * il la déduit de l'accord puis de la tonalité. C'est la seule réponse à
   * cette question dans tout le produit, et la redire ici en produirait une
   * seconde, qui finirait par diverger.
   *
   * Sans octave, parce que le numéro de touche la donne déjà et qu'une octave
   * écrite deux fois est une occasion de les voir se contredire.
   *
   * `null` sur une analyse antérieure au champ.
   */
  spelling: string | null;
  harmony: NoteHarmonyInfo | null;
  sectionId: string | null;
  hand: 'left' | 'right' | null;
};

export type NoteCluster = {
  startTick: number;
  endTick: number;
  notes: NoteSpan[];
  /** Local chord detection, set by the live piano path (PianoControler). */
  chord: DetectedChord | null;
  /**
   * Shared reference to the analyzer chord this cluster belongs to.
   * Both hands playing the same chord point to the *same* AnalysisChord instance.
   * Absent for live piano input.
   */
  harmony?: AnalysisChord;
  /** Annotated notes with harmony info (tone per note). Present when loaded from analyzer API. */
  annotatedNotes?: AnnotatedNote[];
};

export class Part {
  private rawNotes: NoteSpan[] = [];
  private clusters: Timeline<NoteCluster>;
  private role: string | null = null;

  constructor(
    private name: string,
    private instrument: Instrument,
  ) {
    this.clusters = new Timeline();
  }

  setRole(role: string | null): void {
    this.role = role;
  }

  getRole(): string | null {
    return this.role;
  }

  /**
   * Directly set pre-computed clusters (e.g. from the analyzer API).
   * Bypasses quantize/clusterize, the clusters are already fully formed.
   */
  setClusters(clusters: NoteCluster[]): void {
    this.clusters = new Timeline();
    for (const c of clusters) {
      this.clusters.set(c.startTick, c);
    }
  }

  private annotatedNotes: AnnotatedNote[] = [];

  addNote(tick: number, durationTicks: number, velocity: number, note: Note, finger?: number) {
    this.rawNotes.push({ tick, durationTicks, velocity, note, finger });
  }

  /**
   * Store an annotated note (from the analyzer API).
   * The note is also added via addNote() for the standard pipeline;
   * this stores the full annotation for consumers that need harmony/section info.
   */
  addAnnotatedNote(note: AnnotatedNote): void {
    this.annotatedNotes.push(note);
  }

  getAnnotatedNotes(): readonly AnnotatedNote[] {
    return this.annotatedNotes;
  }

  getClusters(): Timeline<NoteCluster> {
    return this.clusters;
  }

  /**
   * Returns all clusters that overlap with [fromTick, toTick):
   * - clusters starting within the range
   * - clusters starting before but still active (endTick > fromTick)
   */
  getClustersInTickRange(fromTick: number, toTick: number): NoteCluster[] {
    const result: NoteCluster[] = [];

    for (const { value: c } of this.clusters.entries()) {
      if (c.startTick >= toTick) break;
      if (c.endTick > fromTick) result.push(c);
    }

    return result;
  }

  /** Convenience: flat list of all NoteSpan in the tick range (derived from clusters). */
  getNotesInTickRange(fromTick: number, toTick: number): NoteSpan[] {
    return this.getClustersInTickRange(fromTick, toTick).flatMap((c) => c.notes);
  }

  getRawNotes(): readonly NoteSpan[] {
    return this.rawNotes;
  }

  getName(): string {
    return this.name;
  }

  getInstrument(): Instrument {
    return this.instrument;
  }
}
